import type { ForwardedRef } from 'react';
import type { InternalUserAuthenticated } from '@ricoh/ria.client.core/model';
import type { Fetcher } from '@ricoh/ria.client.core/plugins/swr/fetcher';

import { useImperativeHandle, useMemo } from 'react';
import { componentDialogSliceActions } from '@ricoh/ria.client.core/plugins/store/slices/component/dialog';
import { createFetcher } from '@ricoh/ria.client.core/plugins/swr/fetcher';
import useSwrOnce from '@ricoh/ria.client.core/plugins/swr/hooks/once';
import { isType } from '@ricoh/ria.client.core/utils/data/object';

import type { AllowedFeaturesData } from '~/common/model/api/license';

import authLogic from '~/common/logic/feature/auth';
import { getUserFetcher } from '~/services/server/identity/account';
import { getUserProfileFetcher } from '~/services/server/identity/profile';
import axiosPlugin from '~/plugins/axios';
import { useStoreDispatch } from '~/plugins/store/hooks/dispatch';
import { authSliceActions } from '~/plugins/store/slices/feature/auth';

import type { AuthorizeData, AuthProviderHooks, AuthProviderProps, AuthProviderRef } from './_.type';

import authConfig from '../_config';

/**
 * Use AuthProvider hooks.
 *
 * @param {AuthProviderProps} _props AuthProvider props.
 * @param {AuthProviderRef} ref AuthProvider ref.
 *
 * @returns {AuthProviderHooks} AuthProvider hooks.
 */
export default function useAuthProviderHooks(_props: AuthProviderProps, ref: ForwardedRef<AuthProviderRef>): AuthProviderHooks {
  const dispatch = useStoreDispatch();

  const applyProfile = authLogic.profile.useApplyProfile();

  const authorizeFetcher = useMemo<Fetcher<never, AuthorizeData>>(
    () =>
      createFetcher([getUserFetcher.key, getUserProfileFetcher.key], async () => {
        const user = await getUserFetcher.handler();

        // * Clear CSRF for new user session
        await axiosPlugin.clearCSRFToken();

        // * Build authorize data
        const routes = authConfig.routes[user.$is];
        const data: AuthorizeData = {
          user,
          routes,
          defaultPath: routes.at(0),
        };

        // * Handle for authorized user
        if (isType<InternalUserAuthenticated>(user, 'user')) {
          const { profile, homePath, allowedFeatures } = await applyProfile();

          data.profile = profile;
          data.defaultPath = homePath;
          data.allowedFeatures = allowedFeatures;
        }

        return data;
      }),
    [applyProfile],
  );

  const authorize = useSwrOnce(authorizeFetcher.key, authorizeFetcher.fetch, {
    onSuccess: ({ user, profile, allowedFeatures }) => {
      dispatch(authSliceActions.internal_setUser(user));
      dispatch(authSliceActions.internal_setProfile(profile));
      dispatch(
        authSliceActions.license_setState(
          allowedFeatures ??
            ({
              packageLevel: 'essential',
              analyticOptions: [],
              azureAiOptions: [],
              natifAiOptions: [],
              iPaaSOptions: undefined,
              singleSignOn: false,
            } as unknown as AllowedFeaturesData),
        ),
      );

      // * Handle for authorized user
      if (isType<InternalUserAuthenticated>(user, 'user')) {
        dispatch(componentDialogSliceActions.session_start());
      } else {
        dispatch(componentDialogSliceActions.session_stop());
      }
    },
  });

  useImperativeHandle(ref, () => ({}));

  return useMemo<AuthProviderHooks>(
    () => ({
      refs: {},

      states: {
        authorize,
      },

      events: {},
    }),
    [authorize],
  );
}
